import { KeyboardEvent, RefObject, useEffect, useRef } from 'react';

import { isTabbable } from '../../JabraFocus';
import { ACCESSIBLE_ELEMENTS_QUERY } from '../MegaMenuConstants';
import { useHeaderNavigation } from '../providers';

type SubmenuTabNavigation = {
  handleContainerKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
};

type NavigationZone = 'majorLinks' | 'subContent' | 'minorLinks';

const NAVIGATION_ZONES: NavigationZone[] = ['majorLinks', 'subContent', 'minorLinks'];

// The dropdown's focusable tab stops, grouped by zone, in tab order.
type ZoneTabStops = Record<NavigationZone, HTMLElement[]>;

/**
 * Describes how Tab navigation behaves for one zone of the dropdown.
 *
 * - `stepWithin`: whether Tab moves between sibling stops inside the zone (true for
 *   sub-content and minor links; false for major links, where any move crosses out).
 * - `leaveZoneForward`: where focus goes when Tab moves past the zone (forward).
 * - `leaveZoneBackward`: where focus goes when Shift+Tab moves past the zone (backward).
 */
type NavigationZoneConfig = {
  stepWithin: boolean;
  leaveZoneForward: (
    event: KeyboardEvent<HTMLDivElement>,
    stops: ZoneTabStops,
    active: HTMLElement
  ) => void;
  leaveZoneBackward: (
    event: KeyboardEvent<HTMLDivElement>,
    stops: ZoneTabStops,
    active: HTMLElement
  ) => void;
};

/**
 * Keyboard Tab management for the desktop mega-menu dropdown.
 *
 * The dropdown's tab order is interleaved (major link -> its sublinks -> its cards
 * -> next major link -> ... -> minor links -> next mega-menu item), which does not
 * match DOM order, so Tab is intercepted and focus is moved imperatively. All of
 * the DOM access required for this focus management is contained in this hook.
 */
export const useSubmenuTabNavigation = (
  containerRef: RefObject<HTMLDivElement | null>
): SubmenuTabNavigation => {
  const { activeItemId, shouldFocus, menuItems, closeMenu, activeMajorLink } =
    useHeaderNavigation();
  // Backward Tab into a not-yet-active major link must first activate it so its
  // sub-content renders; this defers focusing that content until after re-render.
  const pendingFocusLastSubStop = useRef(false);

  useEffect(() => {
    if (!shouldFocus) {
      return;
    }

    const firstFocusable =
      containerRef.current?.querySelector<HTMLElement>(ACCESSIBLE_ELEMENTS_QUERY);
    firstFocusable?.focus();
  }, [activeItemId, shouldFocus, containerRef]);

  const findMenuTrigger = (name: string): HTMLElement | undefined => {
    const scope = containerRef.current?.closest('#header') ?? document;
    return Array.from(scope.querySelectorAll<HTMLElement>('[data-menu-trigger]')).find(
      (trigger) => trigger.dataset.menuTrigger === name
    );
  };

  const getTabStopsWithin = (root: Element | null | undefined): HTMLElement[] =>
    root
      ? Array.from(root.querySelectorAll<HTMLElement>(ACCESSIBLE_ELEMENTS_QUERY)).filter(isTabbable)
      : [];

  const getTabStopsBySelector = (selector: string): HTMLElement[] =>
    containerRef.current
      ? Array.from(containerRef.current.querySelectorAll(selector)).flatMap((group) =>
          getTabStopsWithin(group)
        )
      : [];

  // Single source of truth for each zone's containers, in tab order
  // (sub-content is sublinks first, then cards).
  const ZONE_SELECTORS: Record<NavigationZone, string[]> = {
    majorLinks: ['#majorlinks'],
    subContent: ['#sublinks', '#major-link-cards'],
    minorLinks: ['#minorlinks'],
  };

  // Snapshot every zone's tab stops once, so a single keypress queries the DOM once
  // and both directions of navigation share the same model.
  const collectZoneTabStops = (): ZoneTabStops => ({
    majorLinks: ZONE_SELECTORS.majorLinks.flatMap(getTabStopsBySelector),
    subContent: ZONE_SELECTORS.subContent.flatMap(getTabStopsBySelector),
    minorLinks: ZONE_SELECTORS.minorLinks.flatMap(getTabStopsBySelector),
  });

  const zoneOf = (el: HTMLElement, stops: ZoneTabStops): NavigationZone | null =>
    NAVIGATION_ZONES.find((zone) => stops[zone].includes(el)) ?? null;

  const getActiveMajorIndex = (majorLinks: HTMLElement[]): number =>
    majorLinks.findIndex((el) => el.closest('[aria-current="true"]') !== null);

  // After a major link is activated during backward navigation, move focus to the
  // last element of its freshly rendered sub-content (sublinks + cards).
  useEffect(() => {
    if (!pendingFocusLastSubStop.current) {
      return;
    }
    pendingFocusLastSubStop.current = false;
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const subContentStops = Array.from(
      container.querySelectorAll('#sublinks, #major-link-cards')
    ).flatMap((root) =>
      Array.from(root.querySelectorAll<HTMLElement>(ACCESSIBLE_ELEMENTS_QUERY)).filter(isTabbable)
    );
    subContentStops[subContentStops.length - 1]?.focus();
  }, [activeMajorLink, containerRef]);

  // The navbar action icons (search / UIP / basket) live in the top row *before*
  // the dropdown in the DOM, so default Tab would skip them. Focus the first
  // focusable that follows the last mega-menu trigger inside the top menu.
  const focusNavbarIcons = (event: KeyboardEvent<HTMLDivElement>): void => {
    const topMenu = containerRef.current?.closest('#header')?.querySelector('#top-menu');
    if (!topMenu) return;

    const focusables = Array.from(
      topMenu.querySelectorAll<HTMLElement>(ACCESSIBLE_ELEMENTS_QUERY)
    ).filter(isTabbable);
    const lastTriggerIndex = focusables
      .map((el) => el.hasAttribute('data-menu-trigger'))
      .lastIndexOf(true);
    const firstIcon = focusables[lastTriggerIndex + 1];
    if (!firstIcon) return;

    event.preventDefault();
    closeMenu();
    firstIcon.focus();
  };

  // Moves focus by `offset` within a list of tab stops; returns whether it moved so
  // callers can fall through to cross-zone navigation when there is no neighbour.
  const focusTabStopByOffset = (
    event: KeyboardEvent<HTMLDivElement>,
    tabStops: HTMLElement[],
    active: HTMLElement,
    offset: number
  ): boolean => {
    const index = tabStops.indexOf(active);
    const target = tabStops[index + offset];
    if (index === -1 || !target) return false;

    event.preventDefault();
    target.focus();
    return true;
  };

  // Tab past the last major link's sub-content moves on to the next mega-menu item;
  // if none is left, focus the navbar icons before letting Tab reach the page.
  const focusNextMegaMenuItem = (event: KeyboardEvent<HTMLDivElement>): void => {
    const currentIndex = menuItems.findIndex((item) => item.name === activeItemId);
    const nextItem = menuItems[currentIndex + 1];
    if (!nextItem) {
      focusNavbarIcons(event);
      return;
    }

    const nextTrigger = findMenuTrigger(nextItem.name);
    if (!nextTrigger) return;

    event.preventDefault();
    nextTrigger.focus();
    closeMenu();
  };

  const focusNextMajorLink = (event: KeyboardEvent<HTMLDivElement>, stops: ZoneTabStops): void => {
    const nextMajor = stops.majorLinks[getActiveMajorIndex(stops.majorLinks) + 1];
    if (nextMajor) {
      event.preventDefault();
      nextMajor.focus();
      return;
    }

    if (stops.minorLinks.length) {
      event.preventDefault();
      stops.minorLinks[0].focus();
      return;
    }

    focusNextMegaMenuItem(event);
  };

  // Backward navigation into a major link: activate it (if needed) and land on the
  // last element of its sub-content, mirroring the forward tab order.
  const focusMajorLinkLastSubStop = (stops: ZoneTabStops, targetIndex: number): void => {
    const target = stops.majorLinks[targetIndex];
    if (!target) return;

    if (target.closest('[aria-current="true"]') !== null) {
      const subContentStops = stops.subContent;
      (subContentStops[subContentStops.length - 1] ?? target).focus();
      return;
    }

    pendingFocusLastSubStop.current = true;
    target.focus();
  };

  // Declarative navigation graph: for each zone, how to list its tab stops and where
  // focus goes when Tab falls off either end. `stepWithin` is false for majors
  // because moving from a major always crosses into adjacent content rather than
  // to a sibling major.
  const zoneNavigation: Record<NavigationZone, NavigationZoneConfig> = {
    majorLinks: {
      stepWithin: false,
      leaveZoneForward: (event, stops) => {
        if (stops.subContent.length) {
          event.preventDefault();
          stops.subContent[0].focus();
          return;
        }
        focusNextMajorLink(event, stops);
      },
      leaveZoneBackward: (event, stops, active) => {
        const currentIndex = stops.majorLinks.indexOf(active);
        if (currentIndex > 0) {
          event.preventDefault();
          focusMajorLinkLastSubStop(stops, currentIndex - 1);
          return;
        }
        // First major link: Shift+Tab returns to the trigger that owns this dropdown,
        // instead of walking backwards into the preceding sidebar icons.
        const currentTrigger = findMenuTrigger(activeItemId);
        if (currentTrigger) {
          event.preventDefault();
          currentTrigger.focus();
          closeMenu();
        }
      },
    },
    subContent: {
      stepWithin: true,
      leaveZoneForward: (event, stops) => focusNextMajorLink(event, stops),
      leaveZoneBackward: (event, stops) => {
        const activeMajor = stops.majorLinks[getActiveMajorIndex(stops.majorLinks)];
        if (activeMajor) {
          event.preventDefault();
          activeMajor.focus();
        }
      },
    },
    minorLinks: {
      stepWithin: true,
      leaveZoneForward: (event) => focusNextMegaMenuItem(event),
      leaveZoneBackward: (event, stops) => {
        if (stops.majorLinks.length) {
          event.preventDefault();
          focusMajorLinkLastSubStop(stops, stops.majorLinks.length - 1);
        }
      },
    },
  };

  // Try to move within the current zone; if there is no neighbour in that
  // direction, hand off to the zone's boundary handler that leaves it.
  const moveFocus = (event: KeyboardEvent<HTMLDivElement>, direction: 1 | -1): void => {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return;

    const stops = collectZoneTabStops();
    const zone = zoneOf(active, stops);
    if (!zone) return;

    const zoneConfig = zoneNavigation[zone];
    if (zoneConfig.stepWithin && focusTabStopByOffset(event, stops[zone], active, direction)) {
      return;
    }

    const leaveZone = direction === 1 ? zoneConfig.leaveZoneForward : zoneConfig.leaveZoneBackward;
    leaveZone(event, stops, active);
  };

  // Escape closes the dropdown and returns focus to the trigger that opened it,
  // instead of letting focus fall to the body when the panel unmounts.
  //
  // NOTE: the consumer must attach this via `onKeyDownCapture`, not `onKeyDown`.
  // `useOutsideAlerter` has a native (bubble-phase) keydown listener on the header
  // that closes the menu on Escape; if we listened on bubble too, that listener
  // would unmount this panel before our handler ran. Capture fires first, so we
  // can move focus to the trigger before the panel is torn down.
  const closeAndReturnFocusToTrigger = (event: KeyboardEvent<HTMLDivElement>): void => {
    event.preventDefault();
    findMenuTrigger(activeItemId)?.focus();
    closeMenu();
  };

  const handleContainerKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!containerRef.current) return;

    if (event.key === 'Escape') {
      closeAndReturnFocusToTrigger(event);
      return;
    }

    if (event.key === 'Tab') {
      moveFocus(event, event.shiftKey ? -1 : 1);
    }
  };

  return { handleContainerKeyDown };
};
